/**
 * useOrgCharts - Hook for managing organisation chart trees
 */

import { useCallback, useMemo } from 'react';
import { useSchool } from '../contexts/SchoolContext';
import { useAuth } from '../features/auth/hooks';
import { useRealtimeData } from './useFirebaseData';
import orgChartService from '../services/orgChartService';

export const useOrgCharts = () => {
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const { data, loading } = useRealtimeData('hierarchies');

  const orgCharts = useMemo(
    () => [...(data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [data],
  );

  const createOrgChart = useCallback(async (name) => {
    if (!schoolId || !user) return;
    await orgChartService.createOrgChart(schoolId, { name, createdBy: user.uid });
  }, [schoolId, user]);

  const savePositions = useCallback(async (orgChartId, positions) => {
    if (!schoolId || !user) return;
    await orgChartService.savePositions(schoolId, orgChartId, positions, user.uid);
  }, [schoolId, user]);

  const deleteOrgChart = useCallback(async (orgChartId) => {
    if (!schoolId) return;
    await orgChartService.deleteOrgChart(schoolId, orgChartId);
  }, [schoolId]);

  const setPositionResponsibility = useCallback(async (orgChartId, positionId, responsibility, enabled) => {
    if (!schoolId || !user) throw new Error('Sign in to manage this organisation chart.');
    await orgChartService.setPositionResponsibility(schoolId, orgChartId, positionId, responsibility, enabled, user.uid);
  }, [schoolId, user]);

  return {
    orgCharts, loading,
    createOrgChart, savePositions, deleteOrgChart, setPositionResponsibility,
    refresh: () => {},
    buildTree: orgChartService.buildTree,
    flattenTree: orgChartService.flattenTree.bind(orgChartService),
    generatePositionId: orgChartService.generatePositionId,
  };
};

export default useOrgCharts;
